import React, { useState, useEffect, useRef } from 'react';
import chevronLeft from 'url:../../assets/icons/chevronLeft.svg';
import chevronRight from 'url:../../assets/icons/chevronRight.svg';

interface Props {
  dates: Array<Date>;
  clickDate: Function;
  preSelectedDate: Date | null;
  className?: string;
  inactiveDatesRange?: Array<Date>;
  skipClickMonthChange?: boolean;
}

interface EventDateInterface {
  year: number;
  month: number;
  day: number;
}

const DatePicker: React.FC<Props> = ({ dates, clickDate, preSelectedDate, className, inactiveDatesRange,  skipClickMonthChange}: Props) => {
  const today = new Date();
  const thisDay = preSelectedDate ? preSelectedDate : today;
  const [currentMonth, setCurrentMonth] = useState(thisDay.getMonth());
  const [currentYear, setCurrentYear] = useState(thisDay.getFullYear());
  const [selectedDate, setSelectedDate] = useState<{ day: number; month: number; year: number } | null>(
    preSelectedDate
      ? {
          day: preSelectedDate.getDate(),
          month: preSelectedDate.getMonth(),
          year: preSelectedDate.getFullYear(),
        }
      : null
  );
  const [paddingDays, setPaddingDays] = useState<number[]>([]);
  const [columnGap, setColumnGap] = useState<string>('48px');
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Define event dates
  const eventDates: Array<EventDateInterface> = [];
  for (const d of dates) {
    eventDates.push({ day: d.getDate(), month: d.getMonth(), year: d.getFullYear() });
  }

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  const daysInMonth = (month: number, year: number) => new Date(year, month + 1, 0).getDate();

  const weekDayOfFirst = (month: number, year: number) => {
    let dayOfWeek = new Date(year, month, 1).getDay();
    return dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Monday = 0, Sunday = 6
  };

  const [days, setDays] = useState<number[]>([]);

  useEffect(() => {
    setDays(Array.from({ length: daysInMonth(currentMonth, currentYear) }, (_, i) => i + 1));
    const weekDay = weekDayOfFirst(currentMonth, currentYear);
    setPaddingDays(Array.from({ length: weekDay }, (_, i) => i + 1));
  }, [currentMonth, currentYear]);

  useEffect(() => {
    if (preSelectedDate) {
      const year = preSelectedDate.getFullYear();
      const month = preSelectedDate.getMonth();
      const day = preSelectedDate.getDate();
      setSelectedDate({ day, month, year });
      setCurrentMonth(month);
      setCurrentYear(year);
    }
  }, [preSelectedDate]);

  useEffect(() => {
    if (containerRef.current) {
      const w = containerRef.current.clientWidth;
      if (w < 636) {
        const colGap = w / 6 - 52;
        setColumnGap(colGap + 'px');
      }
    }
  }, [containerRef]);

  // Check if the day has an event
  const hasEvent = (day: number) => {
    return eventDates.some(
      (event) => event.day === day && event.month === currentMonth && event.year === currentYear
    );
  };

  // Check if the day is inactive
  const isInactive = (day: number): boolean => {
    if (!inactiveDatesRange || inactiveDatesRange.length === 0) {
      return false;
    }
    const currentDate = new Date(currentYear, currentMonth, day);
    if (inactiveDatesRange.length === 1) {
      const endDate = inactiveDatesRange[0];
      return currentDate < endDate;
    } else if (inactiveDatesRange.length >= 2) {
      const [startDate, endDate] = inactiveDatesRange.slice(0, 2);
      return currentDate >= startDate && currentDate <= endDate;
    }
    return false;
  };

  // Handle date click event
  const handleDateClick = (day: number) => {
    setSelectedDate({ day, month: currentMonth, year: currentYear });
    clickDate(new Date(currentYear, currentMonth, day));
  };

  // Handle previous month click
  const handlePreviousMonth = () => {
    if (!skipClickMonthChange) clickDate(null);
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((prev) => prev - 1);
    } else {
      setCurrentMonth((prev) => prev - 1);
    }
  };

  // Handle next month click
  const handleNextMonth = () => {
    if (!skipClickMonthChange) clickDate(null);
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((prev) => prev + 1);
    } else {
      setCurrentMonth((prev) => prev + 1);
    }
  };

  // Define classes for each day
  const dayClasses = (day: number): string => {
    const inactive = isInactive(day);
    let classes =
      'relative mb-4 size-10 flex flex-col justify-center items-center border border-transparent font-medium rounded-full';
    if (inactive) {
      classes += ' text-neutral-400 cursor-not-allowed';
    } else {
      if (
        selectedDate &&
        day === selectedDate.day &&
        currentMonth === selectedDate.month &&
        currentYear === selectedDate.year
      ) {
        classes += ' bg-primary-500 text-neutral-800';
      } else {
        classes += ' text-white hover:bg-neutral-700 hover:text-white';
      }
      if (hasEvent(day)) {
        classes += ' border-white';
      }
    }

    return classes;
  };

  // Render today dot
  const todayDot = (day: number) => {
    if (
      day === today.getDate() &&
      currentMonth === today.getMonth() &&
      currentYear === today.getFullYear()
    ) {
      let classes = 'absolute bottom-1 w-0.5 h-0.5 rounded-full';
      if (isInactive(day)) {
        classes += ' bg-neutral-400';
      } else if (
        selectedDate &&
        day === selectedDate.day &&
        currentMonth === selectedDate.month &&
        currentYear === selectedDate.year
      ) {
        classes += ' bg-neutral-800';
      } else {
        classes += ' bg-white hover:bg-white';
      }
      return <span className={classes}></span>;
    }
    return <></>;
  };

  return (
    <div className="sm:flex" ref={containerRef}>
      <div className="space-y-0.5 w-full p-4">
        {/* Month and Year Navigation */}
        <div className="grid grid-cols-5 items-center gap-x-3 pb-3">
          <div className="col-span-1">
            <button
              type="button"
              onClick={handlePreviousMonth}
              className="size-8 flex justify-center items-center text-white hover:bg-neutral-600 rounded-full"
            >
              <img alt="left" src={chevronLeft} className="h-6" />
            </button>
          </div>
          <div className="col-span-3 flex justify-center items-center gap-x-1 text-lg font-medium">
            {months[currentMonth]} {currentYear}
          </div>
          <div className="col-span-1 flex justify-end">
            <button
              type="button"
              onClick={handleNextMonth}
              className="size-8 flex justify-center items-center text-white hover:bg-neutral-600 rounded-full"
            >
              <img alt="right" src={chevronRight} className="h-6" />
            </button>
          </div>
        </div>

        {/* Days of the Week */}
        <div className="grid grid-cols-7 pb-2.5" style={{ columnGap: columnGap }}>
          {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((day) => (
            <span key={day} className="m-px w-10 block text-center font-medium text-white">
              {day}
            </span>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7" style={{ columnGap: columnGap }}>
          {paddingDays.map((paddingDay) => (
            <span key={'p_' + paddingDay}></span>
          ))}
          {days.map((day) => (
            <button
              key={day}
              onClick={() => handleDateClick(day)}
              className={dayClasses(day)}
              disabled={isInactive(day)}
            >
              <span>{day}</span>
              {todayDot(day)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DatePicker;